import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type PointerEvent as ReactPointerEvent } from 'react';
import { t } from '../../i18n/idioma';
import { rotuloTecla, teclaDe, type Acao } from '../../input/atalhos';
import { useAtalhos } from '../../input/useAtalhos';
import type { PartyMemberView, PartyView, RetratoDoMembro } from '../../realtime/cityHub';
import { LinhaEfeitosDe, Vital } from './BarrasVitais';
import { aoRestaurar, gravarJanela, lerJanela, type PosJanela } from './posicoes';
import { useModo } from './useModoTela';

/** O grupo tem 4 vagas contando você: até 3 placas. */
export const MAX_PLACAS_GRUPO = 3;
const JANELA_GRUPO = 'grupo';
const TECLAS: readonly Acao[] = ['alvoGrupo1', 'alvoGrupo2', 'alvoGrupo3'];

/** Os companheiros na ordem das placas (e das teclas Alt+1…3): o grupo menos você. */
export function companheiros(party: PartyView, youName: string): PartyMemberView[] {
  const eu = youName.trim().toLowerCase();
  return party.members.filter(m => m.name.trim().toLowerCase() !== eu).slice(0, MAX_PLACAS_GRUPO);
}

interface Props {
  party: PartyView;
  youName: string;
  /** O jogador marcado como alvo agora (WorldStatus.alvoSelecionado): a placa dele fica amarela. */
  alvoSelecionado: string | null;
  onMarcar(nome: string): void;
  onLeave(): void;
  onKick(nome: string): void;
  retratoDe(canvas: HTMLCanvasElement, nome: string, look: RetratoDoMembro | null): () => void;
  idDoJogador(nome: string): number | null;
}

/**
 * As placas do grupo (techspec-grupo-placas): uma por companheiro, com a mesma interface da sua placa de vida e o
 * retrato à esquerda. Ficam num painel solto, separado da sua placa (Renato, 29/10/2026): o jogador arrasta pela alça
 * para onde ficar melhor, e a posição fica no navegador (posicoes.ts, chave "grupo"). Clicar numa placa marca o
 * companheiro como alvo; a marcada fica com a borda amarela, sem etiqueta.
 */
export function PainelGrupo({ party, youName, alvoSelecionado, onMarcar, onLeave, onKick, retratoDe, idDoJogador }: Props) {
  useAtalhos();
  const modo = useModo();
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<PosJanela | null>(() => lerJanela(modo, JANELA_GRUPO));
  useEffect(() => { setPos(lerJanela(modo, JANELA_GRUPO)); }, [modo]);
  useEffect(() => aoRestaurar(() => setPos(null)), []);

  const arrastar = (ev: ReactPointerEvent<HTMLElement>) => {
    if (ev.button !== 0) return;
    const el = ref.current;
    if (!el) return;
    ev.preventDefault();
    const caixa = el.getBoundingClientRect();
    const dx = ev.clientX - caixa.left;
    const dy = ev.clientY - caixa.top;
    let ultima: PosJanela = { x: caixa.left, y: caixa.top };
    const mover = (e: PointerEvent) => {
      const w = el.offsetWidth, h = el.offsetHeight;
      ultima = {
        x: Math.max(0, Math.min(window.innerWidth - w, e.clientX - dx)),
        y: Math.max(0, Math.min(window.innerHeight - h, e.clientY - dy))
      };
      setPos(ultima);
    };
    const soltar = () => {
      window.removeEventListener('pointermove', mover);
      window.removeEventListener('pointerup', soltar);
      window.removeEventListener('pointercancel', soltar);
      el.classList.remove('is-arrastando');
      gravarJanela(modo, JANELA_GRUPO, ultima);
    };
    el.classList.add('is-arrastando');
    window.addEventListener('pointermove', mover);
    window.addEventListener('pointerup', soltar);
    window.addEventListener('pointercancel', soltar);
  };

  const lista = companheiros(party, youName);
  const marcado = alvoSelecionado?.trim().toLowerCase() ?? '';
  const estilo = pos ? { left: pos.x, top: pos.y } : undefined;
  return (
    <section ref={ref} className={`pg${pos ? '' : ' is-padrao'}`} style={estilo} aria-label={t('grupo.titulo')}>
      <div className="pg__cab">
        <span className="pg__alca" title={t('grupo.arrastar')} aria-hidden="true" onPointerDown={arrastar} />
        <span className="pg__titulo" onPointerDown={arrastar}>{t('grupo.titulo')}</span>
        {/* Sair do GRUPO não é sair da CAÇADA: são dois botões e duas coisas. */}
        <button type="button" className="pg__sair" onClick={onLeave}>{t('grupo.sair')}</button>
      </div>
      {lista.length === 0 && <p className="pg__vazio">{t('grupo.sozinho')}</p>}
      {lista.map((m, i) => (
        <PlacaMembro
          key={m.characterId}
          m={m}
          tecla={TECLAS[i]}
          selecionado={m.name.trim().toLowerCase() === marcado}
          podeExpulsar={party.canKick}
          onMarcar={onMarcar}
          onKick={onKick}
          retratoDe={retratoDe}
          shortId={idDoJogador(m.name)}
        />
      ))}
    </section>
  );
}

function rotuloDoEstado(m: PartyMemberView): string | null {
  if (m.state === 2) return t('grupo.estado.offline');
  if (m.state === 1) return t('grupo.estado.morto');
  if (m.state === 3) return t('grupo.estado.longe', { cidade: m.city });
  if (m.unknown) return t('grupo.estado.semDado');
  return null;
}

function PlacaMembro({ m, tecla, selecionado, podeExpulsar, onMarcar, onKick, retratoDe, shortId }: {
  m: PartyMemberView; tecla: Acao; selecionado: boolean; podeExpulsar: boolean;
  onMarcar(nome: string): void; onKick(nome: string): void;
  retratoDe: Props['retratoDe']; shortId: number | null;
}) {
  const cv = useRef<HTMLCanvasElement>(null);
  // O retrato chega a cada vital do grupo como objeto novo: a chave evita repintar à toa.
  const chaveRetrato = JSON.stringify(m.retrato);
  const retratoRef = useRef(m.retrato);
  retratoRef.current = m.retrato;
  const pintarRef = useRef(retratoDe);
  pintarRef.current = retratoDe;
  useEffect(() => {
    const c = cv.current;
    if (!c) return;
    return pintarRef.current(c, m.name, retratoRef.current);
  }, [m.name, chaveRetrato]);

  const estado = rotuloDoEstado(m);
  const morto = m.state === 1;
  const fora = m.state === 2 || m.state === 3;
  const atalho = teclaDe(tecla);
  const classes = ['pg-m', selecionado && 'is-sel', morto && 'is-morto', (fora || m.unknown) && 'is-off'].filter(Boolean).join(' ');
  const marcar = () => onMarcar(m.name);
  const teclado = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); marcar(); }
  };
  return (
    <div
      className={classes}
      role="button"
      tabIndex={0}
      aria-pressed={selecionado}
      aria-label={t('grupo.marcar', { nome: m.name })}
      onClick={marcar}
      onKeyDown={teclado}
    >
      <canvas ref={cv} className="pg-m__retrato" width={40} height={40} aria-hidden="true" />
      <div className="pg-m__corpo">
        <div className="pg-m__nome">
          {m.leader && <span className="pg-m__coroa" title={t('grupo.lider')} aria-label={t('grupo.lider')}>♛</span>}
          <span className="pg-m__txt">{m.name}</span>
          {atalho && <kbd className="pg-m__tecla">{rotuloTecla(atalho)}</kbd>}
          {podeExpulsar && (
            <button
              type="button"
              className="pg-m__kick"
              title={t('grupo.expulsar')}
              aria-label={`${t('grupo.expulsar')} ${m.name}`}
              onClick={e => { e.stopPropagation(); onKick(m.name); }}
            >
              ×
            </button>
          )}
        </div>
        {estado
          ? <span className="pg-m__estado">{estado}</span>
          : (
            <div className="vt pg-m__barras">
              <Vital cls="hp" v={m.hits} max={m.hitsMax} nome={t('mundo.vida')} />
              {m.manaMax > 0 && <Vital cls="mn" v={m.mana} max={m.manaMax} nome={t('mundo.mana')} />}
              {m.stamMax > 0 && <Vital cls="vg" v={m.stam} max={m.stamMax} nome={t('mundo.vigor')} />}
            </div>
          )}
      </div>
      {!fora && <LinhaEfeitosDe id={shortId} rotulo={t('grupo.efeitos', { nome: m.name })} className="pg-m__buffs" />}
    </div>
  );
}
